/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable @typescript-eslint/ban-types */
/* eslint-disable @typescript-eslint/ban-ts-comment */

import AwsS3 from '@uppy/aws-s3'
import Uppy, { UploadResult, UppyFile, UppyOptions } from '@uppy/core'
import '@uppy/core/dist/style.css'
import '@uppy/dashboard/dist/style.css'
import ImageEditor from '@uppy/image-editor'
import '@uppy/image-editor/dist/style.css'
// @ts-ignore
import French from '@uppy/locales/lib/fr_FR'
import '@uppy/progress-bar/dist/style.css'
import { Dashboard } from '@uppy/react'
import clsx from 'clsx'
import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import ResizeObserver from 'resize-observer-polyfill'

import FlatsyTheme from '@flatsy/core/MuiTheme/flatsyTheme'
import { FilesKind } from '@flatsy/types/Files'
import i18n from '@flatsy/utils/translations'
import { APPLICANT_UPLOAD_URL } from 'src/constants/Api'
import { UpdateApplicantFiles } from 'src/redux/slices/Files/Action'

import { useMobile } from '../Mobile'
import { useStyles } from './Styles'
import { FormatedUppyFiles, IUseUppyUpload, UppyReturn } from './Type'

const TypeWindow = window as any
TypeWindow.ResizeObserver ??= ResizeObserver

export const uppyConf: UppyOptions = {
  restrictions: {
    maxNumberOfFiles: 50,
    maxFileSize: 10 * 1024 * 1024, // 10MB
    allowedFileTypes: ['image/jpg', 'image/png', 'image/jpeg', 'application/pdf'],
  },
}

export const useUppyUpload = ({
  id,
  height = 400,
  callBack,
  callbackAdded,
  applicationId,
  applicantId,
  preDefinedKind,
}: IUseUppyUpload): UppyReturn => {
  const isMobile = useMobile()
  const classes = useStyles()

  const [uppy] = useState(() => {
    const InitUppy = new Uppy(uppyConf)
    InitUppy.use(ImageEditor, { quality: 0.8 })
    InitUppy.use(AwsS3, {
      getUploadParameters(file) {
        return fetch(APPLICANT_UPLOAD_URL(id, InitUppy.getState().meta.applicantId, file.name), {
          method: 'get',
          headers: {
            accept: 'application/json',
            'content-type': 'application/json',
          },
        })
          .then((response) => {
            return response.json()
          })
          .then((data: { result: string; link: string }) => {
            if (data && data.result === 'Success') {
              return {
                method: 'PUT',
                url: data.link,
                headers: {
                  'Content-Type': file.type || '',
                },
              }
            }
            throw new Error('bucket error')
          })
      },
    })

    InitUppy.on('complete', (result) => {
      setUppyResult(result)
    })
    InitUppy.on('upload-success', (file) => {
      updateApplication(file)
    })
    InitUppy.on('files-added', () => {
      refreshFormatedFiles()
      if (callbackAdded) {
        callbackAdded()
      }
    })
    InitUppy.on('file-removed', () => {
      refreshFormatedFiles()
    })
    InitUppy.on('progress', () => {
      // progress: integer (total progress percentage)
    })

    return InitUppy
  })

  const [uppyResult, setUppyResult] = useState<UploadResult | null>(null)
  const [formatedFilesList, setFormatedFilesList] = useState<FormatedUppyFiles | undefined>(
    undefined
  )
  const dispatch = useDispatch()
  const refreshFormatedFiles = () => {
    const uppyMeta = uppy.getState().meta

    const files: UppyFile<{}, {}>[] = uppy.getFiles()

    if (!files) {
      return
    }

    // rebuild formated files list based on uppy files
    const updatedFormatedFilesList: FormatedUppyFiles = Object.fromEntries(
      new Map(
        files.map((file) => [
          file.id,
          {
            name: file.name,
            size: file.data.size,
            type: file.data.type,
            id: file.id,
            previewUrl: URL.createObjectURL(file.data),
            ...(!!uppyMeta.formatedFilesList && uppyMeta.formatedFilesList[file.id]),
            kind:
              uppyMeta.preDefinedKind ||
              (!!uppyMeta.formatedFilesList && uppyMeta.formatedFilesList[file.id]?.kind),
            applicant: uppyMeta.applicantId || '',
          },
        ])
      )
    )
    uppy.setState({
      meta: {
        ...uppy.getState().meta,
        formatedFilesList: updatedFormatedFilesList,
        preDefinedKind: undefined,
      },
    })
    setFormatedFilesList(updatedFormatedFilesList)
  }

  const hasEmptyKind = (): boolean => {
    if (!formatedFilesList) return true
    return Boolean(Object.keys(formatedFilesList).find((fileId) => !formatedFilesList[fileId].kind))
  }

  const updateFormatedFiles = (id: string, kind: FilesKind) => {
    if (!formatedFilesList || !formatedFilesList[id]) {
      return
    }

    const newFormatedFilesList = {
      ...formatedFilesList,
      [id]: {
        ...formatedFilesList[id],
        kind,
      },
    }

    setFormatedFilesList(newFormatedFilesList)
    uppy.setState({
      meta: {
        ...uppy.getState().meta,
        formatedFilesList: newFormatedFilesList,
      },
    })
  }

  const updateApplication = async (file: UppyFile) => {
    const uppyMeta = uppy.getState().meta
    if (uppyMeta && uppyMeta.formatedFilesList) {
      const formated = uppyMeta.formatedFilesList
      const formatedFile = formated[file.id]
      if (formatedFile.kind) {
        dispatch(
          UpdateApplicantFiles({
            application: applicationId,
            files: {
              kind: formatedFile.kind,
              name: file.name,
              size: file.size,
              content_type: file.type || '',
              applicant: formatedFile.applicant,
            },
          })
        )
      }
    }
  }

  const triggerUpload = () => {
    uppy.upload()
  }

  const removeFile = (id: string) => {
    uppy.removeFile(id)
    refreshFormatedFiles()
  }

  const resetFiles = () => {
    uppy.clearUploadedFiles()
  }

  const callBackEvent = () => {
    if (callBack) {
      callBack()
    }
  }

  useEffect(() => {
    uppy.setState({
      meta: {
        ...uppy.getState().meta,
        applicantId,
      },
    })
  }, [applicantId])

  useEffect(() => {
    uppy.setState({
      meta: {
        ...uppy.getState().meta,
        preDefinedKind,
      },
    })
  }, [preDefinedKind])

  useEffect(() => {
    uppy.on('complete', callBackEvent)
    return () => {
      uppy.off('complete', callBackEvent)
    }
  }, [callBack])

  const dropString = () => {
    return isMobile
      ? '%{browseFiles}'
      : i18n.t('drag_and_drop_your_files_here', { browse: `%{browseFiles}` })
  }

  const renderDashboard = (id: string): React.ReactElement => {
    return (
      <div className={classes.main}>
        <Dashboard
          uppy={uppy}
          height={height}
          id={id}
          showLinkToFileUploadResult={false}
          width="100%"
          showSelectedFiles={false}
          hideUploadButton
          hideRetryButton
          locale={{
            strings: {
              ...French.strings,
              dropPasteFiles: dropString(),
              dropHint: `${i18n.t('drag_and_drop_your_files_here', {
                browse: '%{browseFiles}',
              })}`,
              browseFiles: isMobile
                ? i18n.t('import_your_files_by_your_device')
                : i18n.t('import_them_from_your_computer').toLowerCase(),
            },
          }}
        />
        <div className={clsx(classes.inner, 'uppy-illustration')}>
          <img className={classes.illustration} alt="add-file" src={FlatsyTheme.image.addFile} />
        </div>
      </div>
    )
  }

  return {
    element: renderDashboard,
    log: uppyResult,
    triggerUpload,
    formatedFiles: formatedFilesList,
    updateFormatedFiles,
    hasEmptyKind,
    removeFile,
    resetFiles,
  }
}
